import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Plus } from 'lucide-react'
import type { ResearchOrganizationSummary } from '@/types/research'

type Props = {
  organizations: ResearchOrganizationSummary[]
  filters: { search: string; archived: string }
}

export default function ResearchOrganizationsIndex({ organizations, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [deleteTarget, setDeleteTarget] = useState<ResearchOrganizationSummary | null>(null)
  const archivedView = Boolean(filters.archived)

  function applyFilters(next: { archived?: string }) {
    const merged = { search, archived: filters.archived, ...next }
    router.get(
      '/admin/research/organizations',
      Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({})
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/research/organizations/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  return (
    <>
      <Head title="Research - Organizations" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search organizations..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <div className="flex gap-1">
            <Button
              variant={!archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '' })}
            >
              Active
            </Button>
            <Button
              variant={archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '1' })}
            >
              Archived
            </Button>
          </div>
          <Button asChild>
            <Link href="/admin/research/organizations/new">
              <Plus className="mr-2 size-4" />
              New Organization
            </Link>
          </Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Causes</TableHead>
                <TableHead className="text-center">Events</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map(org => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <Link href={`/admin/research/organizations/${org.id}`} className="hover:underline">
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[org.city, org.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{org.budget || '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {org.causes.slice(0, 3).map(cause => (
                        <Badge key={cause} variant="secondary">{cause}</Badge>
                      ))}
                      {org.causes.length > 3 && (
                        <Badge variant="outline">+{org.causes.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">{org.events_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {archivedView ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              router.patch(`/admin/research/organizations/${org.id}/restore`)
                            }
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(org)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button asChild variant="ghost" size="sm">
                            <Link href={`/admin/research/organizations/${org.id}/edit`}>Edit</Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() =>
                              router.patch(`/admin/research/organizations/${org.id}/archive`)
                            }
                          >
                            Archive
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {archivedView ? 'No archived organizations.' : 'No research organizations yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete organization</DialogTitle>
            <DialogDescription>
              Permanently delete &ldquo;{deleteTarget?.name}&rdquo;? This also deletes all of its
              research events and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

ResearchOrganizationsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
